/** @jsxRuntime automatic */
import {
  createContext,
  forwardRef,
  useContext,
  useEffect,
  useId,
  useState,
  type HTMLAttributes,
  type ReactNode,
} from "react";
import { createPortal } from "./portal";
import { cn } from "./utils";
import { useOverlayBehavior } from "./overlay-utils";
import { usePresence } from "./presence";

interface DialogAriaContextValue {
  /** The dialog is playing its exit animation. */
  closing?: boolean;
  titleId?: string;
  descriptionId?: string;
  setTitleId: (id: string | undefined) => void;
  setDescriptionId: (id: string | undefined) => void;
}

const DialogAriaContext = createContext<DialogAriaContextValue>({
  setTitleId: () => {},
  setDescriptionId: () => {},
});

export interface DialogProps {
  open: boolean;
  onOpenChange?: (open: boolean) => void;
  children: ReactNode;
}

export function Dialog({ open, onOpenChange, children }: DialogProps) {
  const [titleId, setTitleId] = useState<string | undefined>();
  const [descriptionId, setDescriptionId] = useState<string | undefined>();
  const { rootRef } = useOverlayBehavior({ open, onOpenChange });
  const { mounted, closing, ref: scrimRef } = usePresence<HTMLDivElement>(open);

  if (!mounted) return null;

  return createPortal(
    <DialogAriaContext.Provider value={{ closing, titleId, descriptionId, setTitleId, setDescriptionId }}>
      <div
        ref={rootRef}
        className={cn("fixed inset-0 z-modal flex items-center justify-center p-4", closing && "pointer-events-none")}
        role="presentation"
        data-state={closing ? "closed" : "open"}
        onClick={() => onOpenChange?.(false)}
      >
        {/* Scrim: 40 % in light, 65 % in dark (--scrim). Fades in and out. */}
        <div
          ref={scrimRef}
          className={cn("absolute inset-0 bg-scrim", closing ? "animate-fade-out" : "animate-fade-in")}
          aria-hidden="true"
        />
        {children}
      </div>
    </DialogAriaContext.Provider>,
    document.body,
  );
}

export const DialogContent = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, children, onClick, ...props }, ref) => {
    const { titleId, descriptionId, closing } = useContext(DialogAriaContext);
    return (
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        tabIndex={-1}
        data-state={closing ? "closed" : "open"}
        // The surface a tray resizes (morphOverlay, spec 07 › Navegación: tray).
        data-sheet-panel="dialog"
        className={cn(
          "relative max-h-[min(90dvh,40rem)] w-full max-w-md overflow-y-auto overscroll-contain rounded-xl bg-popover p-5 text-popover-foreground shadow-modal outline-none",
          closing ? "animate-zoom-out" : "animate-zoom-in",
          className,
        )}
        onClick={(e) => {
          e.stopPropagation();
          onClick?.(e);
        }}
        {...props}
      >
        {children}
      </div>
    );
  },
);
DialogContent.displayName = "DialogContent";

export function DialogHeader({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("space-y-1.5", className)} {...props} />;
}

export function DialogTitle({ className, id, ...props }: HTMLAttributes<HTMLHeadingElement>) {
  const generatedId = useId();
  const titleId = id ?? generatedId;
  const { setTitleId } = useContext(DialogAriaContext);

  useEffect(() => {
    setTitleId(titleId);
    return () => setTitleId(undefined);
  }, [titleId, setTitleId]);

  return (
    <h2
      id={titleId}
      className={cn("font-display text-title font-semibold tracking-tight text-foreground", className)}
      {...props}
    />
  );
}

export function DialogDescription({ className, id, ...props }: HTMLAttributes<HTMLParagraphElement>) {
  const generatedId = useId();
  const descriptionId = id ?? generatedId;
  const { setDescriptionId } = useContext(DialogAriaContext);

  useEffect(() => {
    setDescriptionId(descriptionId);
    return () => setDescriptionId(undefined);
  }, [descriptionId, setDescriptionId]);

  return (
    <p
      id={descriptionId}
      className={cn("text-sm text-muted-foreground", className)}
      {...props}
    />
  );
}
